import { createSlice, PayloadAction, current } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { commonWidgetConfiguration, setChannelDetails } from './helpers';

const initialState = {
    data: { multiDevice: false },
    isLoading: true,
    isCustomWidgetLoaded: {},
    device: undefined,
    keyToTraverse: undefined,
    source: undefined,
    searchPayload: undefined,
    previewNodeModal: {
        isVisible: false,
    },
    saveModalType: undefined,
    node: undefined,
    saveAsCount: 1,
};

const widgetConfigurationSlice = createSlice({
    name: 'widgetConfiguration',
    initialState: initialState,
    reducers: {
        resetWidgetConfiguration: () => {
            return initialState;
        },
        setRoute: (state: RootState, action: PayloadAction<any>) => {
            state.data['route'] = action.payload;
        },
        setSelectedPreviewNode: (state: RootState, action: PayloadAction<any>) => {
            state['node'] = action.payload;
        },
        togglePreviewNodeModal: (state: RootState, action: PayloadAction<any>) => {
            state['previewNodeModal']['isVisible'] = action.payload;
        },
        setWidgetName: (state: RootState, action: PayloadAction<any>) => {
            state.data.widgetName = action.payload;
        },
        setChannelSearchPayload: (state: RootState, action: PayloadAction<any>) => {
            state['searchPayload'] = action.payload;
        },
        setWidgetLoader: (state: RootState, action: PayloadAction<any>) => {
            state.isLoading = action.payload;
        },
        setIsCustomWidgetLoaded: (state: RootState, action: PayloadAction<any>) => {
            Object.keys(action.payload).forEach((widgetTypeId) => {
                state.isCustomWidgetLoaded[widgetTypeId] = action.payload[widgetTypeId];
            });
        },
        setWidgetDevice: (state: any, action: PayloadAction<any>) => {
            state.device = action.payload.data;
        },
        setSaveAsCount: (state: RootState, action: PayloadAction<any>) => {
            state['saveAsCount'] = action.payload;
        },
        setWidgetSource: (state: RootState, action: PayloadAction<any>) => {
            state['source'] = action.payload;
        },
        toggleDeviceLinkMode: (state: RootState) => {
            state['data']['multiDevice'] = !state['data']['multiDevice'];

            if (current(state)?.['data']['secondary']) {
                state['data']['secondary'] = [];
            }
            if (state['data']['multiDevice']) {
                state['source'] = undefined;
            }
        },
        setGaugeWidgetConfiguration: (state: RootState, action: PayloadAction<any>) => {
            state.data = { ...state.data, ...action.payload };
            state.isLoading = false;
        },
        setWidgetConfiguration: (state: RootState, action: PayloadAction<any>) => {
            const payloadClone = setChannelDetails(action);
            const detailsPayload = action.payload.details;
            const payload = commonWidgetConfiguration(payloadClone);
            //This code to be executed in case of command bar widget only to set the tagId in the configuration for the respective channel as command bar widget doesn't have tagId in the configuration payload like other widgets and it is required for executing the commands from the command bar widget.
            if (payload['route']?.state !== undefined && payload['route']?.state === 'command_bar') {
                const allSecondaryChannels = (payload['secondary'] ?? []).flatMap((sec: any, secIndex: number) =>
                    (sec?.channels ?? []).map((channel: any, chanIndex: number) => ({
                        channel,
                        secIndex,
                        chanIndex,
                    }))
                );

                allSecondaryChannels.forEach(({ channel, secIndex, chanIndex }: any) => {
                    const secondaryObj = payload['secondary'][secIndex];
                    if (detailsPayload !== undefined && detailsPayload['data'].length > 0) {
                        for (let i = 0; i < detailsPayload['data']?.length; i++) {
                            const detailsPayloadObj = detailsPayload['data'][i];
                            const objForTagId = detailsPayloadObj?.channels?.filter(
                                (chan: any) => chan.channelId === channel.channelId
                            );

                            if (objForTagId?.length > 0) {
                                secondaryObj['channels'][chanIndex] = {
                                    ...secondaryObj['channels'][chanIndex],
                                    tagId: objForTagId[0]?.tagId,
                                    channelDisplayName:
                                        objForTagId[0]?.channelDisplayName !== null
                                            ? objForTagId[0]?.channelDisplayName
                                            : null,
                                };
                            }
                        }
                    }
                });
            }
            state.data = { ...state.data, ...payload };
            state.isLoading = false;
        },
        setChartConfiguration: (state: RootState, action: PayloadAction<any>) => {
            const payloadClone = setChannelDetails(action);
            payloadClone.secondary.map((item: any) => {
                if (item.value !== 'nan') {
                    item.value = parseInt(item.value);
                }
                return item;
            });
            state.data = { ...state.data, ...payloadClone };
            state.isLoading = false;
        },
        setLoadGroupsChartConfiguration: (state: RootState, action: PayloadAction<any>) => {
            const flattenChannels = (data: any): any => {
                return data?.flatMap((item: any) =>
                    item?.channels?.map((channel: any) => ({
                        ...channel,
                        deviceId: item?.deviceId,
                    }))
                );
            };

            state.data = { ...state.data, loadGroupTrends: flattenChannels(action?.payload?.details?.data) };
            state.isLoading = false;
        },
        setSaveModalType: (state: RootState, action: PayloadAction<any>) => {
            state['saveModalType'] = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase('load_trends/setPredefinedLoadTrends', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('consumption_breakdown/setPredefinedConsumptionBreakdown', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('metrics/setPredefinedMetrics', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('energy_flow/setPredefinedEnergyFlow', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('weather/setPredefinedWeather', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('aggregated_trends/setPredefinedAggregatedTrends', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('details/setPredefinedConfiguration', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('details/setPredefinedGauge', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('trends/setPredefinedTrends', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('loads/setPredefinedLoads', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('load_groups/setPredefinedLoadGroups', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('load_group_trends/setPredefinedLoadGroupTrends', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('loads/setLoadsPreferences', (state: any, action: any) => {
            if (action.payload?.key === 'isAutomated') {
                state['data']['secondary'] = [];
            }
        });
        builder.addCase('load_groups/setLoadGroupsPreferences', (state: any, action: any) => {
            if (action.payload?.key === 'isAutomated') {
                state['data']['secondary'] = [];
            }
        });
        builder.addCase('load_group_trends/setLoadGroupTrendsPreferences', (state: any, action: any) => {
            if (action.payload?.key === 'isAutomated') {
                state['data']['secondary'] = [];
            }
        });
        builder.addCase('charts/setPredefinedChart', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('group_list/setPredefinedGroupListConfiguration', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('device_list/setPredefinedDeviceList', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('map/setPredefinedMapConfiguration', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('timeline/setPredefinedTimelineConfiguration', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('command_bar/setPredefinedCommandBarConfiguration', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('schedule_widget/setPredefinedScheduleConfiguration', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('components/setPredefinedComponent', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase('upcoming/setPredefinedUpcoming', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
        builder.addCase(
            'environmental_stress_monitoring/setPredefinedEnvironmentalStressMonitoring',
            (state, action: any) => {
                state.data.multiDevice = action.payload?.multiDevice;
            }
        );
        builder.addCase('score/setPredefinedScore', (state, action: any) => {
            state.data.multiDevice = action.payload?.multiDevice;
        });
    },
});

export const {
    setRoute,
    setWidgetName,
    setSaveAsCount,
    setWidgetLoader,
    setIsCustomWidgetLoaded,
    setSelectedPreviewNode,
    setWidgetDevice,
    setWidgetSource,
    setSaveModalType,
    toggleDeviceLinkMode,
    setChartConfiguration,
    togglePreviewNodeModal,
    setWidgetConfiguration,
    setChannelSearchPayload,
    resetWidgetConfiguration,
    setGaugeWidgetConfiguration,
    setLoadGroupsChartConfiguration,
} = widgetConfigurationSlice.actions;

export default widgetConfigurationSlice;
